import type { UseQueryOptions } from '@tanstack/react-query';

import { useHistoricalPortfolioSnapshotQuery, useRangeDebugQuery } from '../queries';
import type { AsOfDateSetting } from './types';
import { getAllocationsAsOfDateTimestampFromSetting, getCurrentDate, getLastUploadedDate } from './utils';
import type { AnalysisSubject } from '../analysis';

type QueryOptions = Pick<UseQueryOptions, 'suspense' | 'enabled'>;

type Response = {
  rangeDebugResponse: ReturnType<typeof useRangeDebugQuery>;
  historicalPortfolioSnapshotResponse: ReturnType<typeof useHistoricalPortfolioSnapshotQuery>;
  allocationsAsOfDateTimestamp: number | undefined;
};

export const useHistoricalPortfolioSnapshot = ({
  analysisSubject,
  asOfDateSetting,
  options,
  analysisViewId,
}: {
  analysisSubject: AnalysisSubject | undefined;
  asOfDateSetting: AsOfDateSetting | undefined;
  options?: QueryOptions;
  analysisViewId?: string;
}): Response => {
  const { enabled = true, suspense = false } = options ?? {};

  const rangeDebugResponse = useRangeDebugQuery(
    analysisSubject,
    {
      enabled: !!analysisSubject?.isHistoricalPortfolio && enabled,
      suspense,
    },
    analysisViewId,
  );

  const allocationsAsOfDateTimestamp = asOfDateSetting
    ? getAllocationsAsOfDateTimestampFromSetting({
        asOfDateSetting,
        currentDate: getCurrentDate(
          rangeDebugResponse.data?.historicalPortfolioMetadata?.allSortedAnalysisAllocationDates,
        ),
        lastUploadedDate: getLastUploadedDate(
          rangeDebugResponse.data?.historicalPortfolioMetadata?.allSortedRebalancingDates,
        ),
      })
    : undefined;

  const historicalPortfolioSnapshotResponse = useHistoricalPortfolioSnapshotQuery(
    {
      subject: analysisSubject,
      allocationsAsOfDate: allocationsAsOfDateTimestamp,
    },
    {
      enabled: !!analysisSubject?.isHistoricalPortfolio && !!allocationsAsOfDateTimestamp && enabled,
      suspense,
    },
    analysisViewId,
  );

  return {
    rangeDebugResponse,
    historicalPortfolioSnapshotResponse,
    allocationsAsOfDateTimestamp,
  };
};
